import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart, Users, RotateCcw, AlertTriangle, Package, Megaphone, RefreshCw, ServerCrash, Wifi, UserPlus } from 'lucide-react';
import { fetchAdminDashboardKpi, AdminDashboardKpi } from '../../services/adminDashboardService';

/** "2026-09-08" → "8 settembre 2026". Costruita dai componenti e non da
 * ``new Date(iso)``, che interpreterebbe la stringa come UTC. */
const giornoLungo = (iso: string) => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' });
};

/** "2026-09" → "settembre 2026". */
const meseLungo = (iso: string) => {
  const [y, m] = iso.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('it-IT', { month: 'long', year: 'numeric' });
};

interface KpiCard {
  icon: typeof ShoppingCart;
  label: string;
  value: string;
  /** Cosa sta contando la card: rende leggibile uno zero, che altrimenti
   * si confonde con un guasto. */
  hint: string;
  color: string;
  link: string;
}

const buildKpis = (k: AdminDashboardKpi): KpiCard[] => [
  { icon: ShoppingCart, label: 'Ordini Oggi',    value: String(k.ordiniOggi),  hint: giornoLungo(k.giorno),                       color: 'bg-blue-100 text-blue-700',     link: '/admin/ordini' },
  { icon: ShoppingCart, label: 'Ordini Mese',    value: String(k.ordiniMese),  hint: meseLungo(k.mese),                           color: 'bg-blue-50 text-blue-600',      link: '/admin/ordini' },
  // Non "Clienti Attivi": in questo pannello "attivo" vuol già dire "non
  // bloccato in ERP" nella colonna Stato di /admin/clienti, e CONTEXT.md
  // documenta quanto costano le collisioni di nome in questo progetto.
  { icon: Wifi,         label: 'Online ora',     value: String(k.onlineOra),   hint: `Ultimi ${k.onlineFinestraMinuti} minuti`,   color: 'bg-violet-100 text-violet-700', link: '/admin/clienti?source=online' },
  { icon: UserPlus,     label: 'Da attivare',    value: String(k.clientiDaAttivare), hint: 'Registrazioni in attesa',             color: 'bg-emerald-100 text-emerald-700', link: '/admin/registrazioni' },
  { icon: RotateCcw,    label: 'RMA Pendenti',   value: String(k.rmaPendenti), hint: 'Aperte e in lavorazione',                   color: 'bg-amber-100 text-amber-700',   link: '/admin/rma' },
  { icon: Package,      label: 'Alert Stock',    value: String(k.alertStock),  hint: 'Avvisi disponibilità attivi',               color: 'bg-cyan-100 text-cyan-700',     link: '/admin/alert-stock' },
  { icon: AlertTriangle, label: 'Segnalazioni',  value: String(k.segnalazioni), hint: 'Nuove e in gestione',                      color: 'bg-red-100 text-red-700',       link: '/admin/difformita' },
];

const AZIONI_RAPIDE = [
  { label: 'Gestisci Banner & Promozioni', href: '/admin/banner', icon: Megaphone },
  { label: 'Vedi Alert Stock Attivi', href: '/admin/alert-stock', icon: Package },
  { label: 'Gestisci Pratiche RMA', href: '/admin/rma', icon: RotateCcw },
  { label: 'Impersona un Cliente', href: '/admin/impersona', icon: Users },
];

export default function AdminDashboard() {
  const [kpi, setKpi] = useState<AdminDashboardKpi | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setKpi(await fetchAdminDashboardKpi());
      setError(false);
    } catch {
      // Nessun toast e nessun valore residuo: una dashboard di KPI che
      // mostra numeri vecchi come se fossero attuali è peggio di una che
      // dichiara il guasto.
      setKpi(null);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-6" data-testid="admin-dashboard">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Dashboard Backoffice</h1>
          <p className="text-sm text-muted-foreground mt-0.5" data-testid="dashboard-updated-at">
            {loading
              ? 'Caricamento KPI…'
              : kpi
                ? `Aggiornato alle ${new Date(kpi.generatedAt).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}`
                : 'KPI non disponibili'}
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="flex items-center gap-1.5 border rounded-lg px-3 py-1.5 bg-white text-sm hover:bg-secondary/50 transition-colors disabled:opacity-60"
          data-testid="dashboard-refresh"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          Aggiorna
        </button>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-5 mb-8 flex items-start gap-3" data-testid="dashboard-kpi-error">
          <ServerCrash className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-medium text-red-800">Impossibile calcolare i KPI</p>
            <p className="text-red-700 mt-0.5">
              I contatori non sono stati caricati. Le sezioni del pannello restano
              raggiungibili dal menu e dalle azioni rapide qui sotto.
            </p>
            <button onClick={load} className="mt-2 underline hover:no-underline" data-testid="dashboard-kpi-retry">
              Riprova
            </button>
          </div>
        </div>
      )}

      {!error && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mb-8">
          {kpi
            ? buildKpis(kpi).map(card => (
                <Link key={card.label} to={card.link}>
                  <div
                    className="rounded-xl border bg-white p-5 h-full hover:border-red-300 hover:shadow-sm cursor-pointer transition-all"
                    data-testid={`kpi-card-${card.label.toLowerCase().replace(/ /g, '-')}`}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${card.color}`}>
                        <card.icon className="w-4 h-4" />
                      </div>
                    </div>
                    <div className="text-2xl font-bold">{card.value}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{card.label}</div>
                    <div className="text-[11px] text-muted-foreground/80 mt-1 first-letter:uppercase">{card.hint}</div>
                  </div>
                </Link>
              ))
            : Array.from({ length: 7 }).map((_, i) => (
                <div key={i} className="rounded-xl border bg-white p-5 animate-pulse" data-testid="kpi-card-skeleton">
                  <div className="w-9 h-9 rounded-lg bg-secondary mb-2" />
                  <div className="h-7 w-16 rounded bg-secondary" />
                  <div className="h-3 w-24 rounded bg-secondary mt-2" />
                  <div className="h-2.5 w-20 rounded bg-secondary/70 mt-2" />
                </div>
              ))}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="rounded-xl border bg-white p-5">
          <h2 className="text-sm font-semibold mb-4">Azioni Rapide</h2>
          <div className="space-y-2">
            {AZIONI_RAPIDE.map(a => (
              <Link key={a.href} to={a.href} className="flex items-center gap-3 p-3 rounded-lg hover:bg-secondary/60 transition-colors">
                <a.icon className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm">{a.label}</span>
              </Link>
            ))}
          </div>
        </div>
        <div className="rounded-xl border bg-white p-5">
          <h2 className="text-sm font-semibold mb-4">Note Operative</h2>
          <div className="space-y-3 text-sm text-muted-foreground">
            <p>Questo backoffice consente di gestire i contenuti visibili ai clienti sul portale B2B.</p>
            <p>Le modifiche ai badge prodotto, banner e avvisi sono immediatamente visibili sul sito.</p>
            {/* Rimossa la riga «L'impersonazione cliente viene sempre tracciata
                con audit log»: falsa da febbraio 2026, quando l'audit log delle
                impersonazioni è stato rimosso (migration 016). */}
            <p>
              Ordini Oggi e Ordini Mese contano gli ordini della vista unificata
              (portale + gestionale, senza doppioni): lo stesso totale della pagina Ordini.
            </p>
            <p>
              Online ora conta le aziende, non le persone: titolare e operatori dello
              stesso cliente valgono una presenza. La finestra si regola in Configurazione.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
